import React, { useEffect, useCallback } from 'react';
import "../home-styling/BookingDetailsModals.css";

// Constants for better maintainability
const BOOKING_FIELDS = {
  REFERENCE: ['booking_reference', 'booking_id'],
  STATUS: ['booking_status', 'status'],
  DATE: 'booking_date',
  TRAVEL_DATE: 'travel_date',
  PACKAGE_NAME: 'package_name',
  PEOPLE: ['number_of_people', 'num_people'],
  AMOUNT: 'total_amount',
  CUSTOMER_NAME: 'customer_name',
  CUSTOMER_EMAIL: 'customer_email',
  CUSTOMER_PHONE: 'customer_phone',
  SPECIAL_REQUESTS: 'special_requests',
  PACKAGE_ID: 'package_id',
  OPERATOR_ID: 'operator_id',
  TOUR_GUIDE: 'tour_guide_required',
  EMERGENCY_CONTACT: 'emergency_contact',
  PAYMENT_STATUS: 'payment_status',
  PAYMENT_AMOUNT: 'payment_amount',
  PAYMENT_DATE: 'payment_date',
  PAYMENT_METHOD: 'payment_method'
};

const BookingDetailsModal = React.memo(({ booking, onClose, formatDate, getStatusBadgeClass }) => {
  // Handle ESC key press
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [onClose]);

  // Utility function to get field value with fallbacks
  const getFieldValue = useCallback((fieldOptions) => {
    if (!booking) return '';
    
    if (Array.isArray(fieldOptions)) {
      for (const field of fieldOptions) {
        if (booking[field] !== undefined && booking[field] !== null && booking[field] !== '') {
          return booking[field];
        }
      }
      return '';
    }
    return booking[fieldOptions] || '';
  }, [booking]);

  // Get all booking field values
  const bookingReference = getFieldValue(BOOKING_FIELDS.REFERENCE) || 'N/A';
  const bookingStatus = getFieldValue(BOOKING_FIELDS.STATUS) || 'Unknown';
  const bookingDate = formatDate(getFieldValue(BOOKING_FIELDS.DATE));
  const travelDate = formatDate(getFieldValue(BOOKING_FIELDS.TRAVEL_DATE));
  const packageName = getFieldValue(BOOKING_FIELDS.PACKAGE_NAME) || 'Not specified';
  const numberOfPeople = getFieldValue(BOOKING_FIELDS.PEOPLE) || 'Not specified';
  const totalAmount = getFieldValue(BOOKING_FIELDS.AMOUNT);
  const customerName = getFieldValue(BOOKING_FIELDS.CUSTOMER_NAME) || 'Not specified';
  const customerEmail = getFieldValue(BOOKING_FIELDS.CUSTOMER_EMAIL) || 'Not specified';
  const customerPhone = getFieldValue(BOOKING_FIELDS.CUSTOMER_PHONE) || 'Not specified';
  const specialRequests = getFieldValue(BOOKING_FIELDS.SPECIAL_REQUESTS);
  const packageId = getFieldValue(BOOKING_FIELDS.PACKAGE_ID);
  const operatorId = getFieldValue(BOOKING_FIELDS.OPERATOR_ID);
  const tourGuideRequired = getFieldValue(BOOKING_FIELDS.TOUR_GUIDE);
  const emergencyContact = getFieldValue(BOOKING_FIELDS.EMERGENCY_CONTACT);
  const paymentStatus = getFieldValue(BOOKING_FIELDS.PAYMENT_STATUS);
  const paymentAmount = getFieldValue(BOOKING_FIELDS.PAYMENT_AMOUNT);
  const paymentDate = getFieldValue(BOOKING_FIELDS.PAYMENT_DATE);
  const paymentMethod = getFieldValue(BOOKING_FIELDS.PAYMENT_METHOD);

  // Format amount display
  const formatAmount = useCallback((amount) => {
    if (!amount) return 'Not specified';
    return `₹${amount}`;
  }, []);

  if (!booking) return null;

  return (
    <div className="bdm-modal-overlay" onClick={onClose}>
      <div className="bdm-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="bdm-modal-header">
          <h2>Booking Details</h2>
          {/* <span className="bdm-close-modal" onClick={onClose}>&times;</span> */}
        </div>
        
        <div className="bdm-modal-body">
          {/* Booking Information Section */}
          <div className="bdm-detail-section">
            <h3>Booking Information</h3>
            <div className="bdm-detail-grid">
              <div className="bdm-detail-item">
                <strong>Booking ID:</strong> {bookingReference}
              </div>
              <div className="bdm-detail-item">
                <strong>Status:</strong> 
                <span className={`bdm-status-badge ${getStatusBadgeClass(bookingStatus)}`}>
                  {bookingStatus}
                </span>
              </div>
              <div className="bdm-detail-item">
                <strong>Booking Date:</strong> {bookingDate}
              </div>
              <div className="bdm-detail-item">
                <strong>Travel Date:</strong> {travelDate}
              </div>
            </div>
          </div>

          {/* Package Details Section */}
          <div className="bdm-detail-section">
            <h3>Package Details</h3>
            <div className="bdm-detail-item">
              <strong>Package Name:</strong> {packageName}
            </div>
            <div className="bdm-detail-item">
              <strong>Number of People:</strong> {numberOfPeople}
            </div>
            <div className="bdm-detail-item">
              <strong>Total Amount:</strong> 
              <span className="bdm-amount">
                {formatAmount(totalAmount)}
              </span>
            </div>
          </div>

          {/* Customer Information Section */}
          <div className="bdm-detail-section">
            <h3>Customer Information</h3>
            <div className="bdm-detail-grid">
              <div className="bdm-detail-item">
                <strong>Name:</strong> {customerName}
              </div>
              <div className="bdm-detail-item">
                <strong>Email:</strong> {customerEmail}
              </div>
              <div className="bdm-detail-item">
                <strong>Phone:</strong> {customerPhone}
              </div>
            </div>
          </div>

          {/* Special Requests Section */}
          {specialRequests && (
            <div className="bdm-detail-section">
              <h3>Special Requests</h3>
              <div className="bdm-special-requests-box">
                <p>{specialRequests}</p>
              </div>
            </div>
          )}

          {/* Additional Booking Details Section */}
          {packageId && (
            <div className="bdm-detail-section">
              <h3>Additional Information</h3>
              <div className="bdm-detail-grid">
                <div className="bdm-detail-item">
                  <strong>Package ID:</strong> {packageId}
                </div>
                {operatorId && (
                  <div className="bdm-detail-item">
                    <strong>Operator ID:</strong> {operatorId}
                  </div>
                )}
                {tourGuideRequired !== undefined && tourGuideRequired !== '' && (
                  <div className="bdm-detail-item">
                    <strong>Tour Guide Required:</strong> 
                    <span className={tourGuideRequired ? 'bdm-available' : 'bdm-not-available'}>
                      {tourGuideRequired ? 'Yes' : 'No'}
                    </span>
                  </div>
                )}
                {emergencyContact && (
                  <div className="bdm-detail-item">
                    <strong>Emergency Contact:</strong> {emergencyContact}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment Information Section */}
          {(paymentStatus || paymentAmount) && (
            <div className="bdm-detail-section">
              <h3>Payment Information</h3>
              <div className="bdm-detail-grid">
                {paymentStatus && (
                  <div className="bdm-detail-item">
                    <strong>Payment Status:</strong> 
                    <span className={`bdm-payment-status bdm-${paymentStatus.toLowerCase()}`}>
                      {paymentStatus}
                    </span>
                  </div>
                )}
                {paymentAmount && (
                  <div className="bdm-detail-item">
                    <strong>Payment Amount:</strong> 
                    <span className="bdm-amount">{formatAmount(paymentAmount)}</span>
                  </div>
                )}
                {paymentDate && (
                  <div className="bdm-detail-item">
                    <strong>Payment Date:</strong> {formatDate(paymentDate)}
                  </div>
                )}
                {paymentMethod && (
                  <div className="bdm-detail-item">
                    <strong>Payment Method:</strong> {paymentMethod}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bdm-modal-footer">
          <button className="bdm-btn bdm-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
});

export default BookingDetailsModal;